import {
  Button,
  Divider,
  Flex,
  Icon,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Tag,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';

import { FaRegCreditCard } from 'react-icons/fa6';

import { formatCurrency } from 'views/quote_marketplace/constants';

import { NOT_PROVIDED } from '../../constants';

import type { ActurisAccountSummary } from 'api/acturis/client-center';

interface ClientAccountBadgeProps {
  accountSummary: ActurisAccountSummary;
}

const formatAmount = (value: string | null) =>
  value == null ? NOT_PROVIDED : formatCurrency(value);

const ClientAccountBadge = ({ accountSummary }: ClientAccountBadgeProps) => {
  const labelColor = useColorModeValue('gray.500', 'gray.400');
  const textColor = useColorModeValue('gray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const buttonBg = useColorModeValue('white', 'navy.800');
  const activeBg = useColorModeValue('gray.100', 'gray.700');

  const { outstanding_amount, unallocated_cash, aged_debt } = accountSummary;

  const agedDebtRows = [
    { label: 'Current', value: aged_debt.not_yet_due },
    { label: '1–30 days', value: aged_debt.days_1_to_30 },
    { label: '31–60 days', value: aged_debt.days_31_to_60 },
    { label: '61–90 days', value: aged_debt.days_61_to_90 },
    { label: '90+ days', value: aged_debt.days_91_plus },
  ];

  const renderRow = (label: string, value: string | null, bold = false) => (
    <Flex key={label} justify="space-between" gap="4" py="1">
      <Text
        fontSize="sm"
        color={bold ? textColor : labelColor}
        fontWeight={bold ? '700' : '400'}
      >
        {label}
      </Text>
      <Text fontSize="sm" color={textColor} fontWeight={bold ? '700' : '600'}>
        {formatAmount(value)}
      </Text>
    </Flex>
  );

  return (
    <Popover placement="bottom-end">
      {({ isOpen }) => (
        <>
          <PopoverTrigger>
            <Button
              variant="outline"
              size="sm"
              w="full"
              justifyContent="space-between"
              borderColor={borderColor}
              bg={isOpen ? activeBg : buttonBg}
              color={textColor}
              fontWeight="500"
              rightIcon={
                <ChevronDownIcon
                  transform={isOpen ? 'rotate(180deg)' : undefined}
                  transition="transform 0.2s"
                />
              }
            >
              <Flex align="center" gap="2">
                <Icon as={FaRegCreditCard} color={labelColor} />
                Client Account
                {outstanding_amount != null && (
                  <Tag
                    size="sm"
                    variant="subtle"
                    colorScheme="red"
                    borderRadius="full"
                    fontWeight="600"
                  >
                    {formatCurrency(outstanding_amount)}
                  </Tag>
                )}
              </Flex>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            width="280px"
            _focus={{ boxShadow: 'md' }}
            _focusVisible={{ boxShadow: 'md' }}
          >
            <PopoverArrow />
            <PopoverBody px="4" py="3">
              {renderRow('Outstanding balance', outstanding_amount, true)}
              <Divider my="2" />
              <Text
                fontSize="xs"
                fontWeight="700"
                letterSpacing="wide"
                textTransform="uppercase"
                color={labelColor}
                mb="1"
              >
                Aged debt
              </Text>
              {agedDebtRows.map(({ label, value }) => renderRow(label, value))}
              <Divider my="2" />
              {renderRow('Unapplied cash', unallocated_cash)}
            </PopoverBody>
          </PopoverContent>
        </>
      )}
    </Popover>
  );
};

export default ClientAccountBadge;
